import { BASE_URL } from "../../../Constants/baseConstant";

const getCleanBaseUrl = () => (BASE_URL ? BASE_URL.replace(/\/+$/, "") : "");
export const DEFAULT_MPR_REDIRECT_ROUTE = "/ats/hiring-requests?tab=approvals";
export const getDefaultMprAppLink = () => `${getCleanBaseUrl()}/in/signup?redirect=${encodeURIComponent(DEFAULT_MPR_REDIRECT_ROUTE)}`;

/**
 * Reusable details table component for MPR emails
 */
export const MprDetailsTable = ({ items = [] }) => {
  const validItems = items?.filter(
    (item) => item && item.value !== undefined && item.value !== null && String(item.value).trim() !== ""
  );

  return (
    <table
      width="100%"
      border="0"
      cellPadding="0"
      cellSpacing="0"
      style={{
        marginTop: "16px",
        marginBottom: "20px",
        borderCollapse: "collapse",
        width: "100%",
        backgroundColor: "#ffffff",
        border: "1px solid #e5e7eb",
        borderRadius: "6px",
      }}
    >
      <tbody>
        {validItems.map((item, index) => (
          <tr
            key={index}
            style={{
              borderBottom: index === validItems.length - 1 ? "none" : "1px solid #f3f4f6",
              backgroundColor: index % 2 === 0 ? "#ffffff" : "#f9fafb",
            }}
          >
            <td
              style={{
                padding: "10px 16px",
                fontSize: "14px",
                fontWeight: "bold",
                color: "#4b5563",
                width: "40%",
                verticalAlign: "top",
              }}
            >
              {item?.label}:
            </td>
            <td
              style={{
                padding: "10px 16px",
                fontSize: "14px",
                color: "#111827",
                width: "60%",
                verticalAlign: "top",
              }}
            >
              {String(item?.value) || "-"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

/**
 * Reusable action button for MPR emails
 */
export const MprActionButton = ({ link, text = "View in Meepl" }) => {
  const targetLink = getDefaultMprAppLink();
  return (
    <table width="100%" border="0" cellPadding="0" cellSpacing="0" style={{ margin: "24px 0 12px 0" }}>
      <tbody>
        <tr>
          <td align="center">
            <a
              href={targetLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-block",
                backgroundColor: "#2563eb",
                color: "#ffffff",
                textDecoration: "none",
                fontWeight: "bold",
                fontSize: "14px",
                padding: "12px 28px",
                borderRadius: "5px",
              }}
            >
              {text}
            </a>
          </td>
        </tr>
      </tbody>
    </table>
  );
};

/**
 * Scenario 1: New MPR Created — Notify the First Approver
 */
export const MprCreatedEmailContent = ({
  approverName,
  creatorName,
  mprNo,
  role,
  subRole,
  buName,
  headcount,
  hiringType,
  createdOn,
  appLink,
  isReminder = false,
}) => {
  const items = [
    { label: "MPR Number", value: mprNo },
    { label: "Designation", value: role },
    subRole ? { label: "Role", value: subRole } : null,
    { label: "Department", value: buName },
    { label: "Requested Headcount", value: headcount },
    { label: "Hiring Type", value: hiringType },
    { label: "Requested By", value: creatorName },
    createdOn ? { label: "Requested On", value: createdOn } : null,
  ];

  return (
    <table width="100%" border="0" align="center" cellPadding="0" cellSpacing="0" style={{ maxWidth: "600px", textAlign: "left" }}>
      <tbody>
        <tr>
          <td style={{ padding: "10px 0 0" }}>
            <p style={{ color: "#111827", fontSize: "16px", fontWeight: "bold", margin: "0 0 12px 0" }}>
              Dear {approverName || "Approver"},
            </p>
            <p style={{ color: "#374151", fontSize: "14px", lineHeight: "22px", margin: "0 0 16px 0" }}>
              {isReminder
                ? "This is a reminder that an MPR has been submitted and is awaiting your review."
                : "A new MPR has been created and requires your review."}
            </p>
            <MprDetailsTable items={items} />
            <p style={{ color: "#4b5563", fontSize: "14px", lineHeight: "22px", margin: "16px 0 0 0" }}>
              Please log in to Meepl to review and approve or reject this request.
            </p>
            <MprActionButton text="Review MPR" />
          </td>
        </tr>
      </tbody>
    </table>
  );
};

/**
 * Scenario 3 & 5: Approver Approves the MPR — Notify Next Approver (Level 2 or Level 3)
 */
export const MprApprovalPendingEmailContent = ({
  approverName,
  mprNo,
  role,
  subRole,
  buName,
  headcount,
  level = 2,
  previousApproverName,
  secondApproverName,
  currentStatus,
  appLink,
  isReminder = false,
}) => {
  const levelText = level === 3 ? "Third-Level" : "Second-Level";
  const items = [
    { label: "MPR Number", value: mprNo },
    { label: "Designation", value: role },
    subRole ? { label: "Role", value: subRole } : null,
    { label: "Department", value: buName },
    { label: "Requested Headcount", value: headcount },
    previousApproverName ? { label: "First-Level Approver", value: previousApproverName } : null,
    secondApproverName ? { label: "Second-Level Approver", value: secondApproverName } : null,
    { label: "Current Status", value: currentStatus || `Awaiting ${levelText} Approval` },
  ];

  return (
    <table width="100%" border="0" align="center" cellPadding="0" cellSpacing="0" style={{ maxWidth: "600px", textAlign: "left" }}>
      <tbody>
        <tr>
          <td style={{ padding: "10px 0 0" }}>
            <p style={{ color: "#111827", fontSize: "16px", fontWeight: "bold", margin: "0 0 12px 0" }}>
              Dear {approverName || "Approver"},
            </p>
            <p style={{ color: "#374151", fontSize: "14px", lineHeight: "22px", margin: "0 0 8px 0" }}>
              {isReminder
                ? "This is a reminder that an MPR is awaiting your approval."
                : "A MPR is awaiting your approval."}
            </p>
            <p style={{ color: "#6b7280", fontSize: "13px", lineHeight: "20px", margin: "0 0 16px 0" }}>
              The request has passed the previous approval level(s) and requires your review.
            </p>
            <MprDetailsTable items={items} />
            <p style={{ color: "#4b5563", fontSize: "14px", lineHeight: "22px", margin: "16px 0 0 0" }}>
              Please log in to Meepl to review the request and take the appropriate action.
            </p>
            <MprActionButton text="Review MPR" />
          </td>
        </tr>
      </tbody>
    </table>
  );
};

/**
 * Scenario 2, 4 & 6: Approver Rejects the MPR — Notify Creator
 */
export const MprRejectedEmailContent = ({
  creatorName,
  mprNo,
  role,
  subRole,
  buName,
  rejectedByName,
  approverLevel = "approver",
  comments,
  rejectionDate,
  appLink,
}) => {
  const items = [
    { label: "MPR Number", value: mprNo },
    { label: "Designation", value: role },
    subRole ? { label: "Role", value: subRole } : null,
    { label: "Department", value: buName },
    { label: "Rejected By", value: rejectedByName || "Approver" },
    rejectionDate ? { label: "Rejection Date", value: rejectionDate } : null,
    { label: "Current Status", value: "Rejected" },
    { label: "Comments", value: comments || "No comments provided" },
  ];

  return (
    <table width="100%" border="0" align="center" cellPadding="0" cellSpacing="0" style={{ maxWidth: "600px", textAlign: "left" }}>
      <tbody>
        <tr>
          <td style={{ padding: "10px 0 0" }}>
            <p style={{ color: "#111827", fontSize: "16px", fontWeight: "bold", margin: "0 0 12px 0" }}>
              Dear {creatorName || "Employee"},
            </p>
            <p style={{ color: "#dc2626", fontSize: "14px", fontWeight: "bold", lineHeight: "22px", margin: "0 0 16px 0" }}>
              Your MPR has been rejected by the {approverLevel} approver.
            </p>
            <MprDetailsTable items={items} />
            <p style={{ color: "#4b5563", fontSize: "14px", lineHeight: "22px", margin: "16px 0 0 0" }}>
              Please log in to Meepl to view the details.
            </p>
            <MprActionButton text="View MPR Details" />
          </td>
        </tr>
      </tbody>
    </table>
  );
};

/**
 * Scenario 7: Final Approval (Workflow completed) — Notify Creator
 */
export const MprFinalApprovedEmailContent = ({
  creatorName,
  mprNo,
  role,
  subRole,
  buName,
  headcount,
  finalApproverName,
  approvalDate,
  appLink,
}) => {
  const items = [
    { label: "MPR Number", value: mprNo },
    { label: "Designation", value: role },
    subRole ? { label: "Role", value: subRole } : null,
    { label: "Department", value: buName },
    { label: "Requested Headcount", value: headcount },
    { label: "Final Approver", value: finalApproverName || "Approver" },
    { label: "Final Status", value: "Approved" },
    approvalDate ? { label: "Approval Date", value: approvalDate } : null,
  ];

  return (
    <table width="100%" border="0" align="center" cellPadding="0" cellSpacing="0" style={{ maxWidth: "600px", textAlign: "left" }}>
      <tbody>
        <tr>
          <td style={{ padding: "10px 0 0" }}>
            <p style={{ color: "#111827", fontSize: "16px", fontWeight: "bold", margin: "0 0 12px 0" }}>
              Dear {creatorName || "Employee"},
            </p>
            <p style={{ color: "#16a34a", fontSize: "14px", fontWeight: "bold", lineHeight: "22px", margin: "0 0 8px 0" }}>
              Your MPR has been approved by all required approvers.
            </p>
            <p style={{ color: "#6b7280", fontSize: "13px", lineHeight: "20px", margin: "0 0 16px 0" }}>
              All required approval levels have been completed.
            </p>
            <MprDetailsTable items={items} />
            <p style={{ color: "#4b5563", fontSize: "14px", lineHeight: "22px", margin: "16px 0 0 0" }}>
              Please log in to Meepl to view the final MPR details.
            </p>
            <MprActionButton text="View MPR Details" />
          </td>
        </tr>
      </tbody>
    </table>
  );
};

/**
 * Subject generators for MPR notifications
 */
export const getMprCreatedSubject = (mprNo, role) =>
  `[Action Required] New MPR: ${mprNo || "MPR"}${role ? ` - ${role}` : ""}`;

export const getMprApprovalPendingSubject = (mprNo, role, level = 2) => {
  const levelText = level === 3 ? "Third-Level" : "Second-Level";
  return `[Action Required] MPR Awaiting ${levelText} Approval: ${mprNo || "MPR"}${role ? ` - ${role}` : ""}`;
};

export const getMprRejectedSubject = (mprNo, role) =>
  `MPR Rejected: ${mprNo || "MPR"}${role ? ` - ${role}` : ""}`;

export const getMprFinalApprovedSubject = (mprNo, role) =>
  `MPR Approved: ${mprNo || "MPR"}${role ? ` - ${role}` : ""}`;
